import { useState, useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import Collapsible from '@sdk/components/ui/Collapsible';
import Column from '@sdk/components/ui/spacing/Column';
import gettext from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import useMounted from '@sdk/utils/hooks/useMounted';
import groupIntegrationsByType from '@sdk/integrations/helpers/groupIntegrationsByType';
import View from '@sdk/components/primitives/View';
import FlexCenteredStyle from '@sdk/components/ui/FlexCenteredStyle';
import BrandLoader from '@sdk/components/ui/BrandLoader';
import { subscribe } from '@sdk/utils/websocketPubSub';
import type { IntegrationFromApi } from '@sdk/integrations/types/apiTypes';

import SettingsHeader from './components/SettingsHeader';
import IntegrationListItemHeader from './components/integrations/IntegrationListItemHeader';
import IntegrationListItemContent from './components/integrations/IntegrationListItemContent';

const LoaderContainer = styled(View)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  ${FlexCenteredStyle}
`;

type Props = {
  ConnectCodeModal: any;
  IntegrationParametersModal: any;
};

const IntegrationsScreen: React.FC<React.PropsWithChildren<Props>> = ({
  ConnectCodeModal,
  IntegrationParametersModal,
}) => {
  const api = useSlingApi();
  const mountedRef = useMounted();
  const [integrations, setIntegrations] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [numberOfRefreshes, setNumberOfRefreshes] = useState(0);

  useEffect(() => {
    const unsubscribe = subscribe((message, data) => {
      if (message === 'socket:integration:status') {
        const updatedItegrations = integrations.map((integration) =>
          integration.type === data.platform
            ? { ...integration, enabled: data.enabled, status: data.status }
            : integration
        );
        setIntegrations(updatedItegrations);
      }
    });

    return () => {
      unsubscribe();
    };
  });

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        const { data } = await api.integrations.getAvailableIntegrations();
        setIntegrations(data);
        setIsLoading(false);
      } catch (e) {
        if (mountedRef.current) {
          setIsLoading(false);
          console.info('error while getting available integrations', e);
        }
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfRefreshes]);

  /**
   * Calling this method will trigger API call for most recent list of integrations.
   */
  const refreshListOfIntegrations = (): void => {
    setNumberOfRefreshes(numberOfRefreshes + 1);
  };

  const groupedIntegrations = groupIntegrationsByType(integrations);

  if (isLoading) {
    return (
      <LoaderContainer>
        <BrandLoader />
      </LoaderContainer>
    );
  }

  return (
    <Column>
      <SettingsHeader withSeparator title={gettext('POS apps')} />

      {Boolean(groupedIntegrations?.pos) &&
        groupedIntegrations.pos.map((integration: IntegrationFromApi) => (
          <Collapsible
            key={integration.type}
            padding={20}
            mBottom={20}
            headerItem={
              <IntegrationListItemHeader
                integration={integration}
                ConnectCodeModal={ConnectCodeModal}
                IntegrationParametersModal={IntegrationParametersModal}
                refresh={refreshListOfIntegrations}
              />
            }
            contentItem={<IntegrationListItemContent integration={integration} />}
          />
        ))}
    </Column>
  );
};

export default IntegrationsScreen;
